import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatYuan, getMe, getPublicConfig, resolvePriceCents, type PublicConfig } from '../lib/api';
import { homeCopy as t } from '../content/home';
import { Plate } from '../components/Plate';
import { BeforeAfterPair } from '../components/BeforeAfterPair';
import { SampleLabel } from '../components/SampleLabel';

const IMG = {
  woman: '/assets/images/portrait-woman.jpg',
  womanInput: '/assets/images/portrait-woman-input.jpg',
  group: '/assets/images/family-group.jpg',
  groupExtractedMan: '/assets/images/group-extracted-man.jpg',
};

function SectionHead({
  overline,
  heading,
  sub,
}: {
  overline: string;
  heading: string;
  sub?: string;
}) {
  return (
    <>
      <p className="type-overline text-primary-ink">{overline}</p>
      <h2 className="mt-3 type-display-lg text-ink">{heading}</h2>
      {sub ? <p className="measure mt-3 type-body-md text-ink-secondary">{sub}</p> : null}
    </>
  );
}

export default function Home() {
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);

  const [cfgReady, setCfgReady] = useState(false);

  useEffect(() => {
    getPublicConfig()
      .then(setCfg)
      .catch(() => setCfg(null))
      .finally(() => setCfgReady(true));
    getMe().then((u) => setLoggedIn(!!u)).catch(() => setLoggedIn(false));
  }, []);

  const startTo = loggedIn ? '/order/new' : '/login?next=/order/new';
  const price = (key: 'standard' | 'deep') => {
    if (!cfgReady) return '…';
    return formatYuan(resolvePriceCents(cfg, key));
  };

  const single = t.cases.items[0];
  const group = t.cases.items[1];

  return (
    <>
      {/* Hero */}
      <section className="section">
        <div className="shell hero-grid">
          <div>
            <p className="type-overline text-primary-ink">{t.hero.overline}</p>
            <h1 className="mt-4 type-display-xl text-ink">{t.hero.headline}</h1>
            <p className="measure mt-5 type-body-lg text-ink-secondary">{t.hero.support}</p>
            <div className="mt-8 row-gap-3">
              <Link to={startTo} className="btn-primary">{t.hero.primary}</Link>
              <a href="#pricing" className="btn-secondary">{t.hero.secondary}</a>
            </div>
            <ul className="mt-8 stack-gap-3">
              {t.hero.trust.map((item) => (
                <li key={item} className="row-gap-3 type-label-md text-ink-secondary" style={{ alignItems: 'flex-start' }}>
                  <span aria-hidden="true" className="rule-bullet" style={{ marginTop: 10 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Plate src={IMG.woman} alt={single.title} ratio="tall" lazy={false} />
            <SampleLabel className="mt-3">{t.cases.sampleLabel}</SampleLabel>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="section-tight section-sunken" aria-label={t.pricing.heading}>
        <div className="shell">
          <SectionHead overline={t.pricing.overline} heading={t.pricing.heading} sub={t.pricing.sub} />
          <div className="tier-grid-home">
            {t.pricing.tiers.map((tier, i) => (
              <div key={tier.key} className={`card-tier${i === 1 ? ' card-tier-alt' : ''}`} data-component="card-tier">
                <h3 className="type-display-md text-ink">{tier.name}</h3>
                <p className="mt-3 row-gap-3" style={{ alignItems: 'baseline' }}>
                  <span className="type-display-lg text-ink">{price(tier.key)}</span>
                  <span className="type-label-sm text-ink-muted">{tier.unit}</span>
                </p>
                <p className="mt-3 type-body-md text-ink-secondary">{tier.blurb}</p>
                <ul className="mt-5 stack-gap-3">
                  {tier.includes.map((line) => (
                    <li key={line} className="row-gap-3 type-body-md text-ink-secondary" style={{ alignItems: 'flex-start' }}>
                      <span aria-hidden="true" className="rule-bullet" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="measure mt-6 type-label-sm text-ink-muted">{t.pricing.note}</p>
        </div>
      </section>

      {/* Principle */}
      <section className="section" aria-label={t.principle.overline}>
        <div className="shell">
          <div className="principle-block" data-component="principle-block">
            <p className="type-overline text-primary-ink">{t.principle.overline}</p>
            <p className="measure mt-5 type-principle text-ink">{t.principle.statement}</p>
            <p className="measure mt-5 type-body-md text-ink-secondary">{t.principle.support}</p>
          </div>
        </div>
      </section>

      {/* Cases — real sample images + slider */}
      <section id="cases" className="section" aria-label={t.cases.heading}>
        <div className="shell">
          <SectionHead overline={t.cases.overline} heading={t.cases.heading} sub={t.cases.sub} />
          <div className="case-stack">
            <div>
              <BeforeAfterPair
                before={IMG.womanInput}
                after={IMG.woman}
                beforeAlt={`${single.title} — ${t.cases.beforeLabel}`}
                afterAlt={`${single.title} — ${t.cases.afterLabel}`}
                beforeLabel={t.cases.beforeLabel}
                afterLabel={t.cases.afterLabel}
                sampleLabel={t.cases.sampleLabel}
              />
              <h3 className="mt-4 type-display-md text-ink">{single.title}</h3>
              <p className="measure mt-2 type-body-md text-ink-secondary">{single.desc}</p>
            </div>

            <div>
              <div className="case-extract-grid">
                <div>
                  <Plate
                    src={IMG.group}
                    alt={`${group.title} — ${t.cases.beforeLabel}`}
                    ratio="wide"
                    size="sm"
                  />
                  <p className="mt-2 type-label-sm text-ink-muted">{t.cases.beforeLabel}</p>
                </div>
                <div className="case-extract-after">
                  <Plate
                    src={IMG.groupExtractedMan}
                    alt={`${group.title} — ${t.cases.afterLabel}`}
                    ratio="plate"
                    size="sm"
                  />
                  <p className="mt-2 type-label-sm text-ink-muted">{t.cases.afterLabel}</p>
                </div>
              </div>
              <SampleLabel className="mt-3">{t.cases.sampleLabel}</SampleLabel>
              <h3 className="mt-3 type-display-md text-ink">{group.title}</h3>
              <p className="measure mt-2 type-body-md text-ink-secondary">{group.desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="section-tight section-sunken" aria-label={t.process.heading}>
        <div className="shell">
          <SectionHead overline={t.process.overline} heading={t.process.heading} />
          <ol className="process-grid-home" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {t.process.steps.map((s) => (
              <li key={s.n}>
                <span className="type-display-md text-primary-ink" aria-hidden="true">{s.n}</span>
                <h3 className="mt-2 type-label-md text-ink">{s.title}</h3>
                <p className="mt-2 type-body-md text-ink-secondary">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Privacy */}
      <section id="about-privacy" className="section section-muted" aria-label={t.privacy.heading}>
        <div className="shell">
          <p className="type-overline text-primary-ink">{t.privacy.overline}</p>
          <h2 className="measure mt-3 type-display-lg text-ink">{t.privacy.heading}</h2>
          <ul className="privacy-grid" style={{ listStyle: 'none', padding: 0 }}>
            {t.privacy.items.map((x) => (
              <li key={x} className="row-gap-3 type-body-md text-ink-secondary" style={{ alignItems: 'flex-start' }}>
                <span aria-hidden="true" className="rule-bullet" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <Link to="/legal/privacy" className="mt-6 type-label-md link-plain" style={{ display: 'inline-block' }}>
            {t.privacy.link}
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-tight" aria-label={t.faq.heading}>
        <div className="shell">
          <SectionHead overline={t.faq.overline} heading={t.faq.heading} />
          <div className="mt-8 stack-gap-3" style={{ maxWidth: 720 }}>
            {t.faq.items.map((item, i) => (
              <details key={item.q} className="faq-item" open={i === 0}>
                <summary>
                  <span>{item.q}</span>
                  <span className="faq-glyph" aria-hidden="true">+</span>
                </summary>
                <div style={{ padding: '0 20px 16px' }}>
                  <p className="type-body-md text-ink-secondary">{item.a}</p>
                </div>
              </details>
            ))}
            <a href="#faq" className="mt-6 type-label-md link-plain" style={{ display: 'inline-block' }}>
              {t.faq.link}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
